import { isFile } from "@/hooks/documents";
import { DERIVED_TABLES_FOLDER, type DocumentType, type ReleaseDocument, type ReleaseDocuments } from "@packages/commons";
import { type FileNode, FileType, type TreeData } from "./types";

type Frontier<T = TreeData> = [string, { item: ReleaseDocument; parent?: T }][];

export const buildTree = (documents: ReleaseDocuments): FileNode[] => {
  const root: FileNode = {
    type: FileType.FOLDER,
    name: "Documents",
    children: [],
    id: "/",
  };
  const id = 0;
  // build a frontier where we iterate over all documents to build out the tree
  // should reduce memory footprint and be faster than a recursive solution
  // Derived tables are authored/managed exclusively from Data Model -> Derived Tables, so their folder is
  // hidden. Only that folder is dropped - user folders must stay visible even when empty.
  const frontier: Frontier<FileNode> = Object.entries(documents)
    .filter(([path]) => path !== DERIVED_TABLES_FOLDER)
    .map(([path, item]) => [path, { item }]);
  while (frontier.length > 0) {
    const _n = frontier.shift(); // pop off the stack
    if (!_n) continue; // we are done
    const [path, { item, parent }] = _n;
    const children = (parent?.children as FileNode[]) ?? root.children;
    // if its a file
    if (isFile(item as ReleaseDocument)) {
      // files are leaf nodes, add them to the parent and continue
      const file = item as ReleaseDocument;
      const title = file.name ? file.name.split("/").pop() : path.split("/").pop(); // file is corrupt, try to recover the name
      children.push({
        id: file.reference ?? path,
        name: title && title.length > 0 ? title : "Corrupt document",
        type: FileType.FILE,
        document: file,
      } as FileNode);
      continue;
    }
    // if its a folder, coerce the type
    const folder = item as unknown as ReleaseDocuments;
    const fNode = {
      id: path,
      name: path.split("/").pop(),
      children: [],
      type: FileType.FOLDER,
      folder: folder, // keep reference for easy look up
    } as FileNode;
    children.push(fNode);
    // add children to the frontier
    Object.keys(folder).forEach((child) => {
      frontier.push([`${path}/${child}`, { item: folder[child], parent: fNode }]);
    });
  }

  return root.children as FileNode[];
};

export const sortChildren = (node: TreeData): TreeData => {
  if (!node.children) return node;
  const copy = [...node.children];
  copy.sort((a, b) => {
    if (!!a.children && !b.children) return -1;
    if (!!b.children && !a.children) return 1;
    return a.name < b.name ? -1 : 1;
  });
  const children = copy.map(sortChildren);
  return { ...node, children };
};

export const BLANK_DOC = [];

export const BLANK_SHEET = {
  declarations: [],
  sheets: [],
};

export const BLANK_DOC_NEW = {
  rules: [{ type: "p", text: "" }],
  sections: [],
  source: [{ type: "p", children: [{ text: "" }] }],
};

export const BLANK_DATA_TABLE = {
  headers: ["ID", "ColName"],
  rows: [["1", ""]],
};

export const BLANK_MAPPING = {
  name: "",
  type: "request" as const,
  mode: "json" as const,
  data: {},
};

export const BLANK_CONNECTION = {
  name: "",
  type: "rest" as const,
  url: "https://example.com",
  contentType: "application/json" as const,
  authType: "none" as const,
  timeout: 10000,
  tlsVersion: "1.2" as const,
  verifyCerts: true,
  // SFTP fields
  host: "",
  port: 22,
  path: "",
};

export const getNewFileContents = (type: DocumentType, version = 3) => {
  if (type === "rulesheet") return BLANK_SHEET;
  if (type === "ruledoc" && version > 1) return BLANK_DOC_NEW;
  if (type === "datatable") return BLANK_DATA_TABLE;
  if (type === "mapping") return BLANK_MAPPING;
  if (type === "connection") return BLANK_CONNECTION;
  return BLANK_DOC;
};
